import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import { getCountryObjs } from '@luxuryescapes/lib-countries';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import {
  ForcedRankingsConfig,
  createForcedRankingsConfig,
  updateForcedRankingsConfig,
} from '~/services/PublicOfferFeedService';

import StandAloneMultiRegionsSelect from './StandAloneMultiRegionsSelect';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  fetchData: () => void;
  editableConfig: ForcedRankingsConfig | null;
};

const getCountryOptions = () => {
  const countries = getCountryObjs();
  return Object.values(countries)
    .map((country) => ({
      code: country.iso2,
      label: `${country.countryName} (${country.iso2})`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const countryOptions = getCountryOptions();

const DISTANCE_LOOKUP_OPTIONS = [
  { value: '10km', label: '10km' },
  { value: '100km', label: '100km' },
  { value: '1000km', label: '1000km' },
  { value: 'country_radius', label: 'Country Radius' },
];

export default function ForcedRankingsConfigForm({ open, setOpen, fetchData, editableConfig }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [selectedCountry, setSelectedCountry] = useState<{ code: string; label: string } | null>(null);
  const [distanceLookup, setDistanceLookup] = useState<'10km' | '100km' | '1000km' | 'country_radius'>('10km');
  const [userRegions, setUserRegions] = useState<Array<string>>([]);
  const [id, setId] = useState<string>('');
  const tenant = useCurrentTenant();

  // Form validation
  const [errors, setErrors] = useState<{
    countryCode?: string;
  }>({});

  const isEditMode = !!editableConfig;

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (editableConfig) {
        // Edit mode - populate with existing data
        const countryOption = countryOptions.find((option) => option.code === editableConfig.country_code);
        setSelectedCountry(countryOption || null);
        setDistanceLookup(editableConfig.distance_lookup);
        setUserRegions(editableConfig.user_regions || []);
        setId(editableConfig.id);
      } else {
        // Create mode - reset form
        setSelectedCountry(null);
        setDistanceLookup('10km');
        setUserRegions([]);
      }
      setErrors({});
    }
  }, [open, editableConfig]);

  const validateForm = () => {
    const newErrors: { countryCode?: string } = {};

    if (!selectedCountry) {
      newErrors.countryCode = 'Country is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (!selectedCountry) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        country_code: selectedCountry.code,
        distance_lookup: distanceLookup,
        user_regions: userRegions,
        brand: tenant.tenant.brand,
      };

      if (isEditMode) {
        await updateForcedRankingsConfig(payload, id);
        enqueueSnackbar('Forced rankings config updated successfully', { variant: 'success' });
      } else {
        await createForcedRankingsConfig(payload);
        enqueueSnackbar('Forced rankings config created successfully', { variant: 'success' });
      }

      setOpen(false);
      fetchData();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      enqueueSnackbar(`Failed to ${isEditMode ? 'update' : 'create'} forced rankings config: ${errorMessage}`, {
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit' : 'Create'} Forced Rankings Configuration</DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <Stack spacing={3}>
            <Autocomplete
              options={countryOptions}
              getOptionLabel={(option) => option.label}
              value={selectedCountry}
              onChange={(_, newValue) => {
                setSelectedCountry(newValue);
                if (errors.countryCode && newValue) {
                  setErrors({ ...errors, countryCode: undefined });
                }
              }}
              disabled={isEditMode} // Country code cannot be changed in edit mode
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Offer's Country"
                  error={!!errors.countryCode}
                  helperText={errors.countryCode || 'Select a country from the list'}
                  fullWidth
                />
              )}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              renderOption={(props, option) => (
                <li {...props} key={option.code}>
                  {option.label}
                </li>
              )}
            />

            <StandAloneMultiRegionsSelect
              id="user-regions"
              value={userRegions}
              onChange={setUserRegions}
              label="Website Regions"
            />
            <FormControl fullWidth>
              <InputLabel id="distance-lookup-label">Distance Lookup</InputLabel>
              <Select
                labelId="distance-lookup-label"
                label="Distance Lookup"
                value={distanceLookup}
                onChange={(e) => setDistanceLookup(e.target.value as typeof distanceLookup)}
              >
                {DISTANCE_LOOKUP_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting} variant="text">
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitting} variant="contained" color="primary">
          {isSubmitting ? (isEditMode ? 'Updating...' : 'Creating...') : isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
